import { VALIDATE_REQUIRED } from '@/config';
import { useTranslation } from 'react-i18next';
import { Alert, Form, Input, message, Modal } from 'antd';
import React, { ReactElement } from 'react';
import { SaveOutlined } from '@ant-design/icons';
import { apiSdk } from '@/httpClient';
import { SupervisorPinStatusModel } from '@/__generated';

interface Props {
  status: SupervisorPinStatusModel;
  onClose: (changed: boolean) => void;
}

/**
 * PIN personnel de chef de gare (administrateur ou superviseur d'une gare) : 6 chiffres, saisi sur le terminal du
 * caissier pour valider la 3e réimpression d'un billet. Confirmé par le mot de passe du compte ; un nouveau PIN
 * débloque un PIN bloqué après 5 erreurs.
 */
export default function SupervisorPinModal({ status, onClose }: Props): ReactElement {
  const [t] = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  const handleOk = () => {
    form
      .validateFields()
      .then(v => {
        setLoading(true);
        apiSdk.SupervisorPinService.supervisorPin1({ body: { currentPassword: v.currentPassword, pin: v.pin } })
          .then(res => {
            if (res.success) {
              message.success(t('supervisorPin_Saved'));
              onClose(true);
            } else {
              message.error(t(res.message || ''));
            }
          })
          .catch(err => {
            message.error(err);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => undefined);
  };
  const pinRule = { pattern: /^\d{6}$/, message: t('supervisorPin_SixDigits') };
  return (
    <Modal
      visible={true}
      maskClosable={false}
      width={420}
      className="nta-custom-form"
      title={t('supervisorPin_Title')}
      forceRender={true}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
      onCancel={() => onClose(false)}
      onOk={handleOk}
    >
      <p>{t('supervisorPin_Explanation')}</p>
      {status.blocked ? <Alert type="error" showIcon message={t('supervisorPin_Blocked')} style={{ marginBottom: 16 }} /> : null}
      {!status.blocked && status.hasPin ? <Alert type="info" showIcon message={t('supervisorPin_AlreadySet')} style={{ marginBottom: 16 }} /> : null}
      <Form form={form} layout="vertical">
        <Form.Item name="currentPassword" label={t('updatePassword_CurrentPassword')} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
          <Input.Password className="w-100" autoComplete="current-password" />
        </Form.Item>
        <Form.Item name="pin" label={t('supervisorPin_NewPin')} rules={[{ required: true, message: VALIDATE_REQUIRED }, pinRule]}>
          <Input.Password className="w-100" inputMode="numeric" maxLength={6} autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="repin"
          label={t('supervisorPin_ConfirmPin')}
          dependencies={['pin']}
          rules={[
            { required: true, message: VALIDATE_REQUIRED },
            ({ getFieldValue }) => ({
              validator(rule, value) {
                if (!value || getFieldValue('pin') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(t('supervisorPin_NotMatch'));
              },
            }),
          ]}
        >
          <Input.Password className="w-100" inputMode="numeric" maxLength={6} autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
